import React, { useState, useEffect, useCallback } from 'react';
import { usePagination, SeeMoreButton } from './usePagination';
import { ArchiveButton, ArchivedBadge, RestoreButton, ShowArchivedToggle } from './ArchiveControls';

/**
 * Contractors — agent's roster of trade contractors (2026-06-04, Part 2).
 * Tabbed dashboard with one tab per trade category, an Add Contractor
 * modal, and inline Edit/Delete actions. All requests are agent-scoped
 * on the backend (Rule 10).
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'heating', label: 'Gas & Heating', hint: 'Gas Safe registered — used for gas safety certificates (CP12)' },
  { id: 'electrical', label: 'Electrical' },
  { id: 'plumbing', label: 'Plumbing' },
  { id: 'handyman', label: 'Handyman' },
  { id: 'general', label: 'General' },
  { id: 'energy_assessor', label: 'Energy Assessor (EPC)' },
];

const ORANGE = '#ea580c';
const ORANGE_DARK = '#c2410c';
const ORANGE_TINT = '#fff7ed';

const EMPTY_FORM = {
  company_name: '',
  contact_name: '',
  phone_number: '',
  email: '',
  trade_category: 'general',
  is_inhouse: false,
  notes: '',
};

function ContractorModal({ initial, onSave, onClose, saving }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [err, setErr] = useState(null);

  useEffect(() => { setForm(initial || EMPTY_FORM); }, [initial]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!form.company_name.trim() || !form.contact_name.trim()
        || !form.phone_number.trim() || !form.email.trim()) {
      setErr('Company name, contact name, phone and email are all required.');
      return;
    }
    if (!/^\+[1-9]\d{1,14}$/.test(form.phone_number.trim())) {
      setErr('Phone must be in E.164 format (e.g. +447911123456).');
      return;
    }
    try {
      await onSave(form);
    } catch (e2) {
      setErr(e2.message || 'Could not save.');
    }
  };

  return (
    <div data-testid="contractor-modal"
         style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  zIndex: 90, padding: '2vh 1rem', overflowY: 'auto' }}>
      <form onSubmit={submit}
            style={{ background: '#fff', borderRadius: 14,
                     padding: '1.4rem 1.6rem', width: 'min(520px, 92vw)',
                     maxHeight: '92vh', overflowY: 'auto',
                     boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <h3 style={{ margin: 0, color: '#0f172a' }}>
          {initial?.id ? 'Edit contractor' : 'Add contractor'}
        </h3>
        <p style={{ color: '#64748b', fontSize: '0.85rem', marginTop: 4 }}>
          {initial?.id ? '' : 'Add a new contractor to your roster.'}
        </p>
        <div style={{ display: 'grid', gap: '0.85rem', marginTop: '1rem' }}>
          <Field label="Company name *">
            <input data-testid="ctr-company" type="text" value={form.company_name}
                   onChange={e => setForm({ ...form, company_name: e.target.value })}
                   style={INPUT} />
          </Field>
          <Field label="Contact name *">
            <input data-testid="ctr-contact" type="text" value={form.contact_name}
                   onChange={e => setForm({ ...form, contact_name: e.target.value })}
                   style={INPUT} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
            <Field label="Phone (E.164) *">
              <input data-testid="ctr-phone" type="text" value={form.phone_number}
                     onChange={e => setForm({ ...form, phone_number: e.target.value })}
                     placeholder="+447911123456" style={INPUT} />
            </Field>
            <Field label="Email *">
              <input data-testid="ctr-email" type="email" value={form.email}
                     onChange={e => setForm({ ...form, email: e.target.value })}
                     style={INPUT} />
            </Field>
          </div>
          <Field label="Trade category *">
            <select data-testid="ctr-category" value={form.trade_category}
                    onChange={e => setForm({ ...form, trade_category: e.target.value })}
                    style={INPUT}>
              {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
            {CATEGORIES.find(c => c.id === form.trade_category)?.hint && (
              <span data-testid="ctr-category-hint" style={{ color: '#9a3412', fontSize: '0.8rem' }}>
                {CATEGORIES.find(c => c.id === form.trade_category).hint}
              </span>
            )}
          </Field>
          <Field label="In-house contractor">
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem',
                            color: '#475569', fontSize: '0.9rem' }}>
              <input data-testid="ctr-inhouse" type="checkbox"
                     checked={!!form.is_inhouse}
                     onChange={e => setForm({ ...form, is_inhouse: e.target.checked })} />
              In-house — Estatel AI will share tenant contact details directly.
            </label>
          </Field>
          <Field label="Notes (optional)">
            <textarea data-testid="ctr-notes" value={form.notes || ''}
                      onChange={e => setForm({ ...form, notes: e.target.value })}
                      rows={3} style={{ ...INPUT, resize: 'vertical' }} />
          </Field>
        </div>
        {err && (
          <div data-testid="ctr-err" style={{ marginTop: 12, color: '#b91c1c',
                                              fontSize: '0.85rem' }}>{err}</div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 18, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} disabled={saving}
                  data-testid="ctr-cancel"
                  style={{ background: '#f1f5f9', color: '#0f172a', border: 'none',
                           padding: '0.55rem 1.1rem', borderRadius: 8, cursor: 'pointer' }}>
            Cancel
          </button>
          <button type="submit" disabled={saving} data-testid="ctr-save"
                  style={{ background: ORANGE, color: '#fff', border: 'none',
                           padding: '0.55rem 1.3rem', borderRadius: 8,
                           cursor: saving ? 'not-allowed' : 'pointer',
                           opacity: saving ? 0.7 : 1, fontWeight: 600 }}>
            {saving ? 'Saving…' : (initial?.id ? 'Save changes' : 'Add contractor')}
          </button>
        </div>
      </form>
    </div>
  );
}

const INPUT = {
  width: '100%', padding: '0.55rem 0.7rem', border: '1px solid #cbd5e1',
  borderRadius: 8, fontSize: '0.92rem', fontFamily: 'inherit',
};

function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ color: '#475569', fontSize: '0.82rem', fontWeight: 600 }}>{label}</span>
      {children}
    </label>
  );
}

export default function Contractors({ embeddedHeading = true }) {
  const [active, setActive] = useState('heating');
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [maintAutonomy, setMaintAutonomy] = useState(null);
  const [autonomySaving, setAutonomySaving] = useState(false);
  const [autonomyBanner, setAutonomyBanner] = useState('');
  // 2026-02 FIX 2 — Show archived toggle (off by default).
  const [showArchivedContr, setShowArchivedContr] = useState(false);

  const loadAutonomy = useCallback(async () => {
    try {
      const r = await fetch(`${API}/maintenance/settings`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        setMaintAutonomy(d.maintenance_autonomy || 'managed_by_estateiq');
      }
    } catch (_e) { /* silent */ }
  }, []);

  const saveAutonomy = async (newVal) => {
    setAutonomySaving(true);
    try {
      const r = await fetch(`${API}/maintenance/settings`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ maintenance_autonomy: newVal }),
      });
      if (r.ok) {
        setMaintAutonomy(newVal);
        setAutonomyBanner('Maintenance handling updated');
        setTimeout(() => setAutonomyBanner(''), 3000);
      }
    } finally {
      setAutonomySaving(false);
    }
  };

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/contractors`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load contractors');
      const data = await res.json();
      setItems(data.contractors || []);
      setCounts(data.counts || {});
    } catch (e) { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); loadAutonomy(); }, [reload, loadAutonomy]);

  const tabItems = items.filter(c => c.trade_category === active && (showArchivedContr || !c.archived));
  const contrPager = usePagination(tabItems, 25);

  const onSave = async (form) => {
    if (editing?.contact_first && form.trade_category !== editing.trade_category && !window.confirm(
      `${editing.company_name} is Contact First for their current trade. Changing trade will clear that — you can re-tick them in the new trade.`,
    )) return;
    setSaving(true);
    try {
      const url = editing?.id
        ? `${API}/contractors/${editing.id}`
        : `${API}/contractors`;
      const method = editing?.id ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method, credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          notes: form.notes ? form.notes : null,
        }),
      });
      if (!res.ok) {
        const t = await res.text();
        let detail = t;
        try { detail = JSON.parse(t).detail || t; } catch {}
        throw new Error(Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : detail);
      }
      setModalOpen(false);
      setEditing(null);
      await reload();
    } finally {
      setSaving(false);
    }
  };

  const onContactFirst = async (c, value) => {
    const holder = items.find(x => x.contact_first && x.trade_category === c.trade_category && x.id !== c.id);
    if (value && holder && !window.confirm(
      `${holder.company_name} is currently Contact First for this trade. Replace them with ${c.company_name}?`,
    )) return;
    const res = await fetch(`${API}/contractors/${c.id}/contact-first`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contact_first: value }),
    });
    if (res.ok) reload();
  };

  const onDelete = async (id, name) => {
    if (!window.confirm(`Delete contractor "${name}"? This cannot be undone.`)) return;
    const res = await fetch(`${API}/contractors/${id}`, {
      method: 'DELETE', credentials: 'include',
    });
    if (res.ok) reload();
  };

  return (
    <div data-testid="contractors-page"
         style={{ padding: '1.6rem 2rem', maxWidth: 1200 }}>
      {embeddedHeading && (
        <div style={{ display: 'flex', alignItems: 'center',
                      justifyContent: 'space-between', marginBottom: 18 }}>
          <div>
            <h1 style={{ margin: 0, color: '#0f172a', fontSize: '1.6rem' }}>
              Contractors
            </h1>
            <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: '0.92rem' }}>
              Your roster of trade contractors. Estatel AI contacts them
              automatically when maintenance coordination is set to Estatel AI manages.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <ShowArchivedToggle value={showArchivedContr} onChange={setShowArchivedContr} testid="ctr-show-archived" />
            <button data-testid="ctr-add-btn"
                  onClick={() => { setEditing(null); setModalOpen(true); }}
                  style={{ background: ORANGE, color: '#fff', border: 'none',
                           padding: '0.65rem 1.2rem', borderRadius: 8,
                           cursor: 'pointer', fontWeight: 600 }}>
              + Add Contractor
            </button>
          </div>
        </div>
      )}

      {/* 2026-06-09 — Maintenance Handling toggle. Updates AgentClient.maintenance_autonomy. */}
      <div data-testid="maintenance-autonomy-toggle"
           style={{ display:'flex', alignItems:'center', gap:14, padding:'14px 18px',
                    background:'#fff', border:'1px solid #fed7aa', borderRadius:12,
                    marginBottom: 14 }}>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:700, color:'#0f172a', fontSize:'0.95rem' }}>
            Maintenance coordination
          </div>
          <div style={{ fontSize:'0.82rem', color:'#64748b', marginTop:2 }} data-testid="maintenance-autonomy-scope-note">
            Applies to maintenance contractor coordination only. Section 8, arrears tracking and compliance monitoring carry on either way, and you still receive every alert.
          </div>
        </div>
        <div style={{ display:'flex', background:'#fef3c7', borderRadius:999, padding:4 }}>
          <button data-testid="autonomy-estateiq"
                  disabled={autonomySaving || maintAutonomy === null}
                  onClick={() => saveAutonomy('managed_by_estateiq')}
                  style={{ background: maintAutonomy === 'managed_by_estateiq' ? ORANGE : 'transparent',
                           color: maintAutonomy === 'managed_by_estateiq' ? '#fff' : ORANGE_DARK,
                           border:'none', borderRadius:999, padding:'8px 16px',
                           cursor: autonomySaving ? 'wait' : 'pointer',
                           fontWeight:600, fontSize:'0.85rem' }}>
            Estatel AI manages
          </button>
          <button data-testid="autonomy-agent"
                  disabled={autonomySaving || maintAutonomy === null}
                  onClick={() => saveAutonomy('managed_by_agent')}
                  style={{ background: maintAutonomy === 'managed_by_agent' ? ORANGE : 'transparent',
                           color: maintAutonomy === 'managed_by_agent' ? '#fff' : ORANGE_DARK,
                           border:'none', borderRadius:999, padding:'8px 16px',
                           cursor: autonomySaving ? 'wait' : 'pointer',
                           fontWeight:600, fontSize:'0.85rem' }}>
            Agent manages
          </button>
        </div>
      </div>
      {autonomyBanner && (
        <div data-testid="autonomy-saved-banner"
             style={{ background:'#dcfce7', border:'1px solid #86efac', color:'#166534',
                      padding:'10px 14px', borderRadius:10, marginBottom:12,
                      fontWeight:600, fontSize:'0.88rem' }}>
          ✓ {autonomyBanner}
        </div>
      )}

      {/* Tabs */}
      <div data-testid="ctr-tabs"
           style={{ display: 'flex', gap: 6, borderBottom: '1px solid #fed7aa',
                    background: ORANGE_TINT, borderRadius: '10px 10px 0 0',
                    padding: '0.5rem 0.6rem' }}>
        {CATEGORIES.map(c => {
          const isActive = c.id === active;
          const count = counts[c.id] || 0;
          return (
            <button key={c.id}
                    data-testid={`contractor-tab-${c.id}`}
                    onClick={() => setActive(c.id)}
                    style={{ background: isActive ? ORANGE : 'transparent',
                             color: isActive ? '#fff' : ORANGE_DARK,
                             border: 'none', padding: '0.5rem 0.95rem',
                             borderRadius: 8, fontWeight: 600,
                             cursor: 'pointer', fontSize: '0.9rem',
                             display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {c.label}
              <span style={{ background: isActive ? 'rgba(255,255,255,0.25)' : '#fed7aa',
                             color: isActive ? '#fff' : ORANGE_DARK,
                             padding: '2px 8px', borderRadius: 999,
                             fontSize: '0.72rem', fontWeight: 700 }}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div style={{ background: '#fff', border: '1px solid #fed7aa', borderTop: 'none',
                    borderRadius: '0 0 10px 10px', padding: '1rem 1rem 1.2rem' }}>
        {loading ? (
          <div data-testid="ctr-loading" style={{ padding: 22, color: '#64748b' }}>
            Loading contractors…
          </div>
        ) : tabItems.length === 0 ? (
          <div data-testid={`ctr-empty-${active}`}
               style={{ padding: '2rem 1rem', textAlign: 'center',
                        color: '#64748b' }}>
            No {CATEGORIES.find(c => c.id === active)?.label} contractors yet.
            Click <em>+ Add Contractor</em> to add one.
          </div>
        ) : (
          <>
          <table data-testid={`ctr-table-${active}`}
                 style={{ width: '100%', borderCollapse: 'collapse',
                          fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ background: '#fff7ed', color: '#9a3412',
                           textAlign: 'left' }}>
                <th style={TH}>Company</th>
                <th style={TH}>Contact</th>
                <th style={TH}>Phone</th>
                <th style={TH}>Email</th>
                <th style={TH}>In-house</th>
                <th style={TH} title="On compliance jobs, this contractor is emailed first in their trade">Contact first</th>
                <th style={{ ...TH, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {contrPager.visible.map(c => (
                <tr key={c.id} data-testid={`ctr-row-${c.id}`}
                    style={{ borderBottom: '1px solid #f1f5f9', opacity: c.archived ? 0.6 : 1 }}>
                  <td style={TD}>
                    {c.company_name}
                    {c.archived && <span style={{ marginLeft: 8 }}><ArchivedBadge small /></span>}
                  </td>
                  <td style={TD}>{c.contact_name}</td>
                  <td style={TD}>{c.phone_number}</td>
                  <td style={TD}>{c.email}</td>
                  <td style={TD}>
                    {c.is_inhouse ? (
                      <span style={{ background: '#dcfce7', color: '#166534',
                                     padding: '2px 8px', borderRadius: 999,
                                     fontWeight: 600, fontSize: '0.75rem' }}>
                        In-house
                      </span>
                    ) : (
                      <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Outsourced</span>
                    )}
                  </td>
                  <td style={TD}>
                    <input type="checkbox" data-testid={`ctr-contact-first-${c.id}`}
                           checked={!!c.contact_first} disabled={!!c.archived}
                           onChange={e => onContactFirst(c, e.target.checked)}
                           style={{ width: 16, height: 16, cursor: c.archived ? 'not-allowed' : 'pointer',
                                    accentColor: ORANGE_DARK }} />
                  </td>
                  <td style={{ ...TD, textAlign: 'right' }}>
                    {c.archived ? (
                      <RestoreButton kind="contractor" id={c.id}
                                      onRestored={() => setItems(prev => prev.map(x => x.id === c.id ? { ...x, archived: false } : x))} />
                    ) : (
                      <>
                        <button data-testid={`ctr-edit-${c.id}`}
                                onClick={() => { setEditing(c); setModalOpen(true); }}
                                style={{ ...LINK_BTN, color: ORANGE_DARK }}>Edit</button>
                        <span style={{ marginLeft: 12, display: 'inline-block' }}>
                          <ArchiveButton kind="contractor" id={c.id} size="xs"
                                          testid={`ctr-archive-${c.id}`}
                                          onArchived={() => setItems(prev => prev.map(x => x.id === c.id ? { ...x, archived: true } : x))} />
                        </span>
                        <button data-testid={`ctr-del-${c.id}`}
                                onClick={() => onDelete(c.id, c.company_name)}
                                style={{ ...LINK_BTN, color: '#b91c1c', marginLeft: 12 }}>Delete</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <SeeMoreButton hasMore={contrPager.hasMore} remaining={contrPager.remaining} onClick={contrPager.showMore} testid="contractors" />
        </>
        )}
      </div>

      {modalOpen && (
        <ContractorModal initial={editing}
                         saving={saving}
                         onSave={onSave}
                         onClose={() => { setModalOpen(false); setEditing(null); }} />
      )}
    </div>
  );
}

export { ContractorModal };

const TH = { padding: '0.55rem 0.6rem', fontSize: '0.78rem', fontWeight: 700,
             textTransform: 'uppercase', letterSpacing: '0.04em' };
const TD = { padding: '0.7rem 0.6rem', color: '#0f172a', verticalAlign: 'middle' };
const LINK_BTN = { background: 'none', border: 'none', cursor: 'pointer',
                   fontWeight: 600, fontSize: '0.88rem' };
